/*
 * Theme set (issue 314): the dark/light boolean generalised to a small NAMED
 * set — dark (the default), light, high-contrast, and a colorblind-safe
 * palette for anything colour-coded. This module is the ONE writer of the
 * choice after load; the pre-paint reader is client/public/theme-bootstrap.js,
 * which applies the same class + attribute before first paint (see its header
 * for why that lives in its own classic script — the CSP drops inline script,
 * and the no-flash property is load-bearing). The two share a vocabulary and
 * storage shape; config/__tests__/theme.test.js executes the bootstrap file
 * against this module's output to keep them from drifting apart, exactly as
 * typography.test.js does for the #199 attributes.
 *
 * STORAGE. localStorage['theme'] keeps its own key (the #199 decision — it is
 * read by the load-bearing first lines of the bootstrap) and its shipped
 * values keep their shipped meaning: absent = dark, 'light' = light,
 * 'dark' = dark. The two new values extend the same key, so a pre-314
 * browser's stored choice still reads correctly and a pre-314 CLIENT reading
 * a post-314 value degrades to dark (its `!== 'light'` check), never to junk.
 *
 * DOCUMENT SHAPE. Tailwind's dark: variants and every `.dark` rule key off
 * the `dark` class, so each theme declares a BASE (dark or light) via that
 * class, and the two non-boolean themes additionally stamp
 * `data-theme="<name>"` on <html>, whose token overrides live in index.css.
 * The default (dark) document carries no data-theme attribute — byte-identical
 * to every pre-314 snapshot, the applyTypography convention.
 */

export const THEMES = ['dark', 'light', 'high-contrast', 'colorblind'];

export const DEFAULT_THEME = 'dark';

export const THEME_LABELS = {
  dark: 'Dark',
  light: 'Light',
  'high-contrast': 'High contrast',
  colorblind: 'Colorblind-safe',
};

/** Themes built on the dark base (they carry the `dark` class so Tailwind
 *  dark: variants and `.dark` rules apply beneath their token overrides). */
export const DARK_BASED = ['dark', 'high-contrast', 'colorblind'];

const KEY = 'theme';

/** The stored choice, validated — an unknown or corrupted value degrades to
 *  the default (dark), which is also what the bootstrap's own guard does. */
export function getTheme() {
  let stored = null;
  try {
    stored = localStorage.getItem(KEY);
  } catch (err) {
    stored = null;
  }
  return THEMES.includes(stored) ? stored : DEFAULT_THEME;
}

/** Apply a (validated) theme to <html> — the same class + attribute the
 *  pre-paint bootstrap sets, so a change after load and a fresh load agree. */
export function applyTheme(theme) {
  const name = THEMES.includes(theme) ? theme : DEFAULT_THEME;
  const el = document.documentElement;
  if (DARK_BASED.includes(name)) el.classList.add('dark');
  else el.classList.remove('dark');
  if (name === 'high-contrast' || name === 'colorblind') {
    el.setAttribute('data-theme', name);
  } else {
    el.removeAttribute('data-theme');
  }
  return name;
}

/** Persist and apply a choice. Unlike typography, an explicit 'dark' is
 *  STORED rather than clearing the key — the shipped toggle has always
 *  written 'dark', and #291's sign-in merge reads the key's presence as
 *  explicitness. Returns the validated theme. */
export function setTheme(theme) {
  const name = THEMES.includes(theme) ? theme : DEFAULT_THEME;
  try {
    localStorage.setItem(KEY, name);
  } catch (err) {
    /* private mode etc. — the in-page change still applies */
  }
  return applyTheme(name);
}
